import { FilterRowSelection, RelationalOperator } from '../../types/filter';
import {
  AggregationDefinition,
  ControlType,
  CsvMetadata,
  DatabaseMetadata,
  FilterDefinitionFields,
  StaffSeedMetadata,
} from './FilterDefinitionFields';
import { FilterDefinitionValidator } from './FilterDefinitionValidator';

export class FilterDefinition implements FilterDefinitionFields {
  version?: string;
  label?: string;
  toolTip?: string;
  controlType?: ControlType;
  isAdvancedFilter?: boolean;
  dateFormat?: string;
  availableOperators?: RelationalOperator[];
  databaseMetadata?: DatabaseMetadata;
  csvMetadata: CsvMetadata;
  staffSeedMetadata?: StaffSeedMetadata;
  labelMapping?: Record<string, string>;
  defaultOptions?: FilterRowSelection['value'];
  defaultValue?: FilterRowSelection['value'];
  defaultRelationalOperator?: RelationalOperator;
  valueSortFn?: (value: (string | null)[]) => string[];
  isGroupBy?: boolean;
  aggregation?: AggregationDefinition;
  isArrayField?: boolean;

  private constructor(filterDefinition: FilterDefinitionFields) {
    this.csvMetadata = filterDefinition.csvMetadata;
    // Copy all fields from the input object to `this` in one line:
    Object.assign(this, filterDefinition);
  }

  /**
   * A unique identifier for this filter field based on its CSV metadata.
   */
  get id(): string {
    return `${this.csvMetadata.columnName}_${this.csvMetadata.columnOrder}`;
  }

  /**
   * Indicates if this field is a database-backed field.
   */
  get isDatabaseField(): boolean {
    return this.csvMetadata?.fieldType === 'database';
  }

  /**
   * Indicates if this field's controlType corresponds to an aggregate selection (like SELECT or MULTI_SELECT).
   */
  get isAggregateField(): boolean {
    return (
      this.controlType === ControlType.SELECT ||
      this.controlType === ControlType.MULTI_SELECT ||
      this.controlType === ControlType.SEARCHABLE_MULTI_SELECT
    );
  }

  /**
   * Finds a filter definition by its ID within an array of filter definitions.
   * @param filterDefinitions - The list of filter definitions to search.
   * @param id - The ID to find.
   * @returns The matching FilterDefinition or undefined.
   */
  static findById(
    filterDefinitions: FilterDefinition[],
    id: string
  ): FilterDefinition | undefined {
    return filterDefinitions.find(
      filterDefinition => filterDefinition.id === id
    );
  }

  /**
   * Deserializes a plain input object into a FilterDefinition instance.
   */
  static deserialize(
    filterDefinitionJson: FilterDefinitionFields
  ): FilterDefinition {
    return new FilterDefinition(filterDefinitionJson);
  }

  /**
   * Creates an array of FilterDefinitions from their input representations.
   * Optionally sets a version on all created FilterDefinitions.
   */
  static createFilterDefinitions(
    filterDefinitionsJson: FilterDefinitionFields[],
    version?: string
  ): FilterDefinition[] {
    const filterDefinitions = filterDefinitionsJson.map(fdJson => {
      const fd = FilterDefinition.deserialize(fdJson);
      if (version) {
        fd.version = version;
      }
      return fd;
    });

    FilterDefinitionValidator.validateFilterDefinitions(filterDefinitions);
    return filterDefinitions;
  }

  /**
   * Simplifies filter definitions by omitting user-facing properties like `label`, `toolTip`, `controlType`, `labelMapping`.
   */
  static simplify(
    filterDefinitions: FilterDefinition[]
  ): Array<
    Omit<
      FilterDefinitionFields,
      'label' | 'toolTip' | 'controlType' | 'labelMapping'
    >
  > {
    return filterDefinitions.map(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ({ label, toolTip, controlType, labelMapping, ...rest }) => rest
    );
  }
}
